/**
 * HoldingRowActions — per-row actions for trustee-recorded assets.
 *
 * Used by /properties, /companies and the JSE rail on /portfolios:
 *   - Revalue : APPEND a new valuation (history-preserving, A4). Step-up gated.
 *   - History : the asset's valuation timeline + last-valued / next-due /
 *               source / stale (A1).
 *   - Edit    : correct the display name / market value in place (A3).
 *   - Delete  : soft-delete (cascades the whole timeline server-side).
 * Edit, Delete and Revalue are step-up gated server-side — a STEP_UP_REQUIRED
 * response surfaces a guided message.
 *
 * Valuations Wave 1 (F3, 2026-07-04):
 *   - the revalue modal carries a BASIS select (what the number is based on —
 *     defaults to Owner estimate) and a "this is a historical value" toggle
 *     that sends `backfill: true` with the past asAt (§8.3), so a backdated
 *     point never masquerades as the latest;
 *   - the history modal shows each point's basis, marks backfilled rows
 *     "added retrospectively" (§8.3) and firm act-as rows "Updated by your
 *     adviser" (§8.2 — never silent).
 * The revalue modal is exported (`RevalueHoldingModal`) so the dashboard's
 * "Values needing attention" card (F4) opens the SAME flow one-click.
 *
 * Discoverability fix (2026-08-01): Beno asked for "a pop-up showing previous
 * valuations with dates" — that was already the History action above, but he
 * never found it. It was labelled "History" (doesn't say what it holds) and
 * styled in the most muted colour of the four row actions, so it read as the
 * throwaway option when it's one of the most useful. Now:
 *   - the entry point reads "Valuation history" and uses full `--ink`
 *     (was `--ink-2`), on par with Edit rather than fading into the row;
 *   - an optional `lastValuedAt` prop, when the caller already has the date
 *     in hand, renders "Last valued <date>" immediately beside the link so
 *     the fact advertises itself without a click and the link has an
 *     obvious anchor;
 *   - `ValuationHistoryModal` is exported (same pattern as
 *     `RevalueHoldingModal`) so the dashboard holdings table and the "Values
 *     needing attention" card can open the SAME modal one-click, instead of
 *     a second one being built.
 */

import { useState } from 'react';
import { ApiError } from '../lib/api/client';
import {
  useDeleteTrustHolding,
  usePatchTrustHolding,
  useRevalueTrustHolding,
  useHoldingValuations,
  inferValuationBasis,
  valuationBasisLabel,
  type ValuationBasis,
  type ValuationSource,
} from '../lib/api';
import { formatRand } from '../lib/fmt-r';
import { useToastStore } from '../lib/toast-store';
import { mapError } from '../lib/error-messages';

interface Props {
  trustId: string;
  holdingId: string;
  name: string;
  /** Current market value in ZAR cents. */
  marketValueZar: number;
  /** Noun used in the confirm copy, e.g. "property", "company", "holding". */
  noun?: string;
}

function stepUpMessage(err: unknown): string {
  if (err instanceof ApiError && err.isStepUpRequired) {
    return 'Re-authentication required. Refresh your sign-in and try again.';
  }
  return mapError(err, 'save');
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Exported (2026-08-01) so callers that already hold a date (e.g. a
 *  dashboard row's own `asAt`) can format it identically to this file's
 *  modal — see the `lastValuedAt` prop on `HoldingRowActions` below. */
export function fmtDate(iso: string | null): string {
  if (!iso) return 'Not on file';
  return new Date(iso).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Whole-Rand display.
 *
 * This was a local formatter using an ASCII space, which made it the FIFTH
 * un-unified currency formatter in a codebase whose canonical one
 * (`lib/fmt-r.ts`) opens by recording that it was written to kill the other
 * four. Its own header names the failure: "a single trust could render the same
 * money two different ways on two screens."
 *
 * The SA fiduciary format uses U+202F, a narrow no-break space, for both the
 * "R" gap and the thousands separator. An ASCII space is a different character
 * that looks almost identical, which is why this survived a currency-separator
 * fix on 25 August that only repaired the dashboard. Delegated rather than
 * re-implemented, so there is one place to change it.
 */
const fmtRand = (rand: number): string => formatRand(rand);

function sourceLabel(s: ValuationSource): string {
  switch (s) {
    case 'manual': return 'Manual';
    case 'valuation-feed': return 'Valuation feed';
    case 'imported-csv': return 'CSV import';
    case 'broker-api': return 'Broker feed';
  }
}

/** The basis choices offered in the revalue modal — Owner estimate first (the
 *  default); feed-only bases (AVM / market price) stay selectable for a manual
 *  transcription of a feed number. */
const BASIS_OPTIONS: ValuationBasis[] = [
  'owner-estimate',
  'purchase-price',
  'afs',
  'municipal-valuation',
  'professional-appraisal',
  'market-price',
  'indexed-estimate',
  'avm',
];

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 9000,
  background: 'rgba(15,15,15,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};
const panel: React.CSSProperties = {
  background: 'var(--surface, #fff)', borderRadius: 10, padding: 24,
  width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
};
const fieldInput: React.CSSProperties = {
  width: '100%', padding: '9px 11px', fontSize: 14,
  border: '1px solid var(--hairline-2)', borderRadius: 6,
  background: 'var(--bg, #fff)', color: 'var(--ink)', boxSizing: 'border-box',
};
const fieldLabel: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6,
};
// Exported (2026-08-01) so the dashboard holdings table and the "Values
// needing attention" card can render a "Valuation history" link that matches
// this file's own row actions exactly, instead of drifting to a near-copy.
export const linkBtn: React.CSSProperties = {
  background: 'none', border: 'none', padding: '0 2px',
  fontSize: 12.5, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2,
  fontFamily: 'var(--sans)',
};

/**
 * Revalue modal (A4 + Wave-1 F3) — value + date + BASIS + the historical-value
 * (backfill) toggle. Exported standalone so the dashboard's "Values needing
 * attention" card opens the same flow without the row's other actions.
 */
export function RevalueHoldingModal({
  trustId,
  holdingId,
  name,
  marketValueZar,
  noun = 'asset',
  onClose,
}: Props & { onClose: () => void }) {
  const [revalValue, setRevalValue] = useState(String(Math.round(marketValueZar / 100)));
  const [revalDate, setRevalDate] = useState(todayIso());
  const [basis, setBasis] = useState<ValuationBasis>('owner-estimate');
  const [historical, setHistorical] = useState(false);
  const revalue = useRevalueTrustHolding();
  const pushToast = useToastStore((s) => s.pushToast);

  function handleRevalue() {
    const v = Number(revalValue);
    if (!Number.isFinite(v) || !Number.isInteger(v) || v <= 0) {
      pushToast({ kind: 'error', message: 'Enter a positive whole-Rand value.' });
      return;
    }
    if (historical && revalDate >= todayIso()) {
      pushToast({ kind: 'error', message: 'A historical value needs a date in the past.' });
      return;
    }
    revalue.mutate(
      {
        trustId,
        holdingId,
        marketValueRand: v,
        basis,
        ...(revalDate ? { asAt: revalDate } : {}),
        // §8.3: explicit backfill intent — the point is stamped retrospective
        // and never becomes "latest" by a same-day createdAt tiebreak.
        ...(historical ? { backfill: true } : {}),
      },
      {
        onSuccess: () => { onClose(); pushToast({ kind: 'success', message: `New valuation recorded for ${name}.` }); },
        onError: (err) => pushToast({ kind: 'error', message: stepUpMessage(err) }),
      },
    );
  }

  return (
    <div role="dialog" aria-modal="true" aria-label={`Revalue ${noun}`} style={overlay}>
      <div style={panel}>
        <div style={{ fontSize: 18, fontFamily: 'var(--serif)', color: 'var(--ink)', marginBottom: 6 }}>
          Record a new valuation
        </div>
        <p style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5, marginBottom: 16 }}>
          <b>{name}</b> is currently valued at {fmtRand(Math.round(marketValueZar / 100))}. Recording a new
          valuation keeps the previous value in the asset&rsquo;s history.
        </p>
        <label style={fieldLabel}>
          New market value (ZAR, whole Rands)
        </label>
        <input
          aria-label="New market value"
          type="number" min={1} step={1}
          value={revalValue}
          onChange={(e) => setRevalValue(e.target.value)}
          style={{ ...fieldInput, marginBottom: 14 }}
        />
        <label style={fieldLabel}>
          What is this value based on?
        </label>
        <select
          aria-label="Valuation basis"
          data-testid="revalue-basis"
          value={basis}
          onChange={(e) => setBasis(e.target.value as ValuationBasis)}
          style={{ ...fieldInput, marginBottom: 14 }}
        >
          {BASIS_OPTIONS.map((b) => (
            <option key={b} value={b}>{valuationBasisLabel(b)}</option>
          ))}
        </select>
        <label style={fieldLabel}>
          Valuation date
        </label>
        <input
          aria-label="Valuation date"
          type="date"
          value={revalDate}
          onChange={(e) => setRevalDate(e.target.value)}
          style={{ ...fieldInput, marginBottom: 12 }}
        />
        <label
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 18,
            fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.45, cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            aria-label="This is a historical value"
            data-testid="revalue-backfill"
            checked={historical}
            onChange={(e) => setHistorical(e.target.checked)}
            style={{ marginTop: 2 }}
          />
          <span>
            This is a <b>historical</b> value (backfill). It adds a past point to the
            timeline and is marked &ldquo;added retrospectively&rdquo;. It never replaces the
            current value.
          </span>
        </label>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} disabled={revalue.isPending}
            style={{ padding: '8px 14px', border: '1px solid var(--hairline-2)', background: 'transparent', color: 'var(--ink)', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
            Cancel
          </button>
          <button type="button" data-testid="confirm-revalue-holding" onClick={handleRevalue} disabled={revalue.isPending}
            style={{ padding: '8px 14px', border: 'none', background: 'var(--accent-600, #9a6a1e)', color: '#fff', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: revalue.isPending ? 'default' : 'pointer', opacity: revalue.isPending ? 0.7 : 1 }}>
            {revalue.isPending ? 'Recording…' : 'Record valuation'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function HoldingRowActions({
  trustId, holdingId, name, marketValueZar, noun = 'asset',
  // Optional (2026-08-01): when the caller already has the holding's
  // last-valued date (properties/companies/portfolios screens all carry it
  // as `h.asAt`), passing it here surfaces it inline next to the entry
  // point instead of the fact being invisible until a click.
  lastValuedAt = null,
}: Props & { lastValuedAt?: string | null }) {
  const [mode, setMode] = useState<null | 'delete' | 'edit' | 'revalue' | 'history'>(null);
  const [editName, setEditName] = useState(name);
  const [editValue, setEditValue] = useState(String(Math.round(marketValueZar / 100)));
  const del = useDeleteTrustHolding();
  const patch = usePatchTrustHolding();
  const pushToast = useToastStore((s) => s.pushToast);

  function openEdit() {
    setEditName(name);
    setEditValue(String(Math.round(marketValueZar / 100)));
    setMode('edit');
  }

  function handleDelete() {
    del.mutate(
      { trustId, holdingId },
      {
        onSuccess: () => { setMode(null); pushToast({ kind: 'success', message: `${name} removed.` }); },
        onError: (err) => pushToast({ kind: 'error', message: stepUpMessage(err) }),
      },
    );
  }

  function handleEdit() {
    const v = Number(editValue);
    const body: { assetName?: string; marketValueRand?: number } = {};
    const trimmed = editName.trim();
    if (trimmed && trimmed !== name) body.assetName = trimmed;
    if (Number.isFinite(v) && Number.isInteger(v) && v > 0 && v !== Math.round(marketValueZar / 100)) {
      body.marketValueRand = v;
    }
    if (!body.assetName && body.marketValueRand === undefined) {
      setMode(null);
      return;
    }
    patch.mutate(
      { trustId, holdingId, patch: body },
      {
        onSuccess: () => { setMode(null); pushToast({ kind: 'success', message: `${trimmed || name} updated.` }); },
        onError: (err) => pushToast({ kind: 'error', message: stepUpMessage(err) }),
      },
    );
  }

  return (
    <>
      <div style={{ display: 'inline-flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" data-testid={`revalue-holding-${holdingId}`} onClick={() => setMode('revalue')} style={{ ...linkBtn, color: 'var(--accent-600, #9a6a1e)' }}>
          Revalue
        </button>
        {lastValuedAt && (
          <span data-testid={`last-valued-${holdingId}`} style={{ fontSize: 12, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>
            Last valued {fmtDate(lastValuedAt)}
          </span>
        )}
        {/* Discoverability fix (2026-08-01): was "History" in the most muted
            of the four tones (--ink-2) — read as the throwaway option. Now
            says what it holds and reads at Edit's weight (full --ink). */}
        <button type="button" data-testid={`history-holding-${holdingId}`} onClick={() => setMode('history')} style={{ ...linkBtn, color: 'var(--ink)' }}>
          Valuation history
        </button>
        <button type="button" onClick={openEdit} style={{ ...linkBtn, color: 'var(--primary-700)' }}>
          Edit
        </button>
        <button
          type="button"
          data-testid={`delete-holding-${holdingId}`}
          onClick={() => setMode('delete')}
          style={{ ...linkBtn, color: 'var(--negative)' }}
        >
          Delete
        </button>
      </div>

      {mode === 'revalue' && (
        <RevalueHoldingModal
          trustId={trustId}
          holdingId={holdingId}
          name={name}
          marketValueZar={marketValueZar}
          noun={noun}
          onClose={() => setMode(null)}
        />
      )}

      {mode === 'history' && (
        <ValuationHistoryModal trustId={trustId} holdingId={holdingId} name={name} onClose={() => setMode(null)} />
      )}

      {mode === 'delete' && (
        <div role="dialog" aria-modal="true" aria-label={`Remove ${noun}`} style={overlay}>
          <div style={panel}>
            <div style={{ fontSize: 18, fontFamily: 'var(--serif)', color: 'var(--ink)', marginBottom: 8 }}>
              Remove this {noun}?
            </div>
            <p style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.55, marginBottom: 18 }}>
              <b>{name}</b> will be removed from this trust&rsquo;s asset register and will no
              longer appear on the portfolio. Its full valuation history is removed too. The change
              is recorded on the audit chain.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setMode(null)}
                disabled={del.isPending}
                style={{ padding: '8px 14px', border: '1px solid var(--hairline-2)', background: 'transparent', color: 'var(--ink)', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="confirm-delete-holding"
                onClick={handleDelete}
                disabled={del.isPending}
                style={{ padding: '8px 14px', border: 'none', background: 'var(--negative)', color: '#fff', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: del.isPending ? 'default' : 'pointer', opacity: del.isPending ? 0.7 : 1 }}
              >
                {del.isPending ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      {mode === 'edit' && (
        <div role="dialog" aria-modal="true" aria-label={`Edit ${noun}`} style={overlay}>
          <div style={panel}>
            <div style={{ fontSize: 18, fontFamily: 'var(--serif)', color: 'var(--ink)', marginBottom: 16 }}>
              Edit {noun}
            </div>
            <label style={fieldLabel}>
              Name
            </label>
            <input
              aria-label="Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              maxLength={200}
              style={{ ...fieldInput, marginBottom: 14 }}
            />
            <label style={fieldLabel}>
              Market value (ZAR, whole Rands)
            </label>
            <input
              aria-label="Market value"
              type="number"
              min={1}
              step={1}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              style={{ ...fieldInput, marginBottom: 18 }}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setMode(null)}
                disabled={patch.isPending}
                style={{ padding: '8px 14px', border: '1px solid var(--hairline-2)', background: 'transparent', color: 'var(--ink)', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="confirm-edit-holding"
                onClick={handleEdit}
                disabled={patch.isPending}
                style={{ padding: '8px 14px', border: 'none', background: 'var(--primary-700)', color: '#fff', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: patch.isPending ? 'default' : 'pointer', opacity: patch.isPending ? 0.7 : 1 }}
              >
                {patch.isPending ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** Valuation timeline modal (A1). Sparkline + table + last-valued/next-due/
 *  basis/source. Wave-1 marks: "added retrospectively" on backfilled points
 *  (§8.3) and "Updated by your adviser" on firm act-as points (§8.2).
 *  Exported (2026-08-01) so the dashboard holdings table and the "Values
 *  needing attention" card can reuse this SAME modal — see the file header. */
export function ValuationHistoryModal({ trustId, holdingId, name, onClose }: {
  trustId: string; holdingId: string; name: string; onClose: () => void;
}) {
  const q = useHoldingValuations({ trustId, holdingId });
  const data = q.data;
  const series = data?.series ?? [];
  // series is newest-first; chart oldest-first.
  const chronological = [...series].reverse();
  const maxVal = chronological.reduce((m, p) => Math.max(m, p.marketValueRand), 0) || 1;

  return (
    <div role="dialog" aria-modal="true" aria-label={`Valuation history for ${name}`} style={overlay}>
      <div style={{ ...panel, maxWidth: 560 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
          <div style={{ fontSize: 18, fontFamily: 'var(--serif)', color: 'var(--ink)' }}>Valuation history</div>
          <button type="button" aria-label="Close" onClick={onClose} style={{ ...linkBtn, color: 'var(--ink-3)', fontSize: 18, textDecoration: 'none' }}>×</button>
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 16 }}>{name}</div>

        {q.isLoading ? (
          <div aria-busy="true" style={{ padding: 24, color: 'var(--ink-3)', fontSize: 13 }}>Loading valuation history…</div>
        ) : q.isError ? (
          <div role="alert" style={{ padding: 16, background: 'var(--negative-soft)', border: '1px solid var(--negative)', color: 'var(--negative)', borderRadius: 6, fontSize: 13 }}>
            Could not load valuation history. Please try again in a moment.
          </div>
        ) : series.length === 0 ? (
          <div role="status" style={{ padding: 20, color: 'var(--ink-2)', fontSize: 13.5, textAlign: 'center' }}>
            No valuations recorded yet. Use &ldquo;Revalue&rdquo; to record the first.
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 16 }}>
              <Stat label="Last valued" value={fmtDate(data!.lastValuedAt)} />
              <Stat label="Next due" value={fmtDate(data!.nextDueAt)} badge={data!.stale ? 'Overdue' : undefined} />
              <Stat label="Basis" value={valuationBasisLabel(inferValuationBasis(data!.latestSource, data!.latestBasis))} />
              <Stat label="Source" value={sourceLabel(data!.latestSource)} />
            </div>

            {/* Mini bar sparkline */}
            <div aria-hidden="true" style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 56, marginBottom: 16, padding: '0 2px' }}>
              {chronological.map((p) => (
                <div key={p.holdingId + p.asAt} title={`${fmtDate(p.asAt)} · ${fmtRand(p.marketValueRand)}`}
                  style={{
                    flex: 1, minWidth: 6,
                    height: `${Math.max(6, Math.round((p.marketValueRand / maxVal) * 56))}px`,
                    background: p.source === 'manual' ? 'var(--primary-500)' : 'var(--accent-500)',
                    borderRadius: '2px 2px 0 0',
                  }} />
              ))}
            </div>

            <table className="table-head" style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ padding: '6px 4px' }}>Date</th>
                  <th style={{ padding: '6px 4px', textAlign: 'right' }}>Value</th>
                  <th style={{ padding: '6px 4px' }}>Basis</th>
                  <th style={{ padding: '6px 4px' }}>Source</th>
                </tr>
              </thead>
              <tbody>
                {series.map((p) => (
                  <tr key={p.holdingId + p.asAt} style={{ borderTop: '1px solid var(--hairline)' }}>
                    <td style={{ padding: '7px 4px', color: 'var(--ink-2)' }}>{fmtDate(p.asAt)}</td>
                    <td style={{ padding: '7px 4px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--ink)' }}>{fmtRand(p.marketValueRand)}</td>
                    <td style={{ padding: '7px 4px', color: 'var(--ink-3)' }}>
                      {valuationBasisLabel(inferValuationBasis(p.source, p.basis))}
                      {/* §8.3: a backfilled point is always visibly marked. */}
                      {p.retrospective ? (
                        <div style={{ fontSize: 11, fontStyle: 'italic', color: 'var(--ink-4)' }}>added retrospectively</div>
                      ) : null}
                    </td>
                    <td style={{ padding: '7px 4px', color: 'var(--ink-3)' }}>
                      {sourceLabel(p.source)}
                      {/* §8.2: a firm-written point is flagged on the client's view. */}
                      {p.viaFirmActAs ? (
                        <div style={{ fontSize: 11, fontStyle: 'italic', color: 'var(--ink-4)' }}>Updated by your adviser</div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
          <button type="button" onClick={onClose}
            style={{ padding: '8px 14px', border: '1px solid var(--hairline-2)', background: 'transparent', color: 'var(--ink)', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, badge }: { label: string; value: string; badge?: string }) {
  return (
    <div>
      <div className="eyebrow ink" style={{ marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 14, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 6 }}>
        {value}
        {badge ? (
          <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--negative)', background: 'var(--negative-soft)', border: '1px solid var(--negative)', borderRadius: 4, padding: '1px 6px' }}>
            {badge}
          </span>
        ) : null}
      </div>
    </div>
  );
}
